<div class="tpf-page">
  <div class="tpf-save-bar">
    <div class="tpf-save-bar__inner">
      <div class="tpf-save-bar__left">
        <button
          type="button"
          class="tpf-save-bar__back"
          [attr.aria-label]="'testPlans.form.back' | translate"
          (click)="onCancel()"
        >
          <mat-icon fontSet="material-icons-outlined" aria-hidden="true">chevron_left</mat-icon>
        </button>
        <div class="tpf-save-bar__heading">
          <span class="tpf-save-bar__eyebrow">
            @if (isEdit && plan()) {
              {{ 'testPlans.form.eyebrowEdit' | translate: { id: plan()!.publicId } }}
            } @else {
              {{ 'testPlans.form.eyebrowNew' | translate }}
            }
          </span>
          <span class="tpf-save-bar__title">
            {{ (isEdit ? 'testPlans.form.editTitle' : 'testPlans.form.createTitle') | translate }}
          </span>
        </div>
        @if (dirty() || selectionDirty()) {
          <span class="tpf-dirty-pill">
            <span class="tpf-dirty-pill__dot" aria-hidden="true"></span>
            {{ 'testPlans.form.unsavedChanges' | translate }}
          </span>
        }
      </div>
      @if (!isReadOnly()) {
        <div class="tpf-save-bar__right">
          <button mat-stroked-button type="button" (click)="onCancel()">
            {{ 'testPlans.form.cancel' | translate }}
          </button>
          <button mat-flat-button color="primary" type="button" [disabled]="saveDisabled()" (click)="onSave()">
            <mat-icon aria-hidden="true">check</mat-icon>
            {{ (isEdit ? 'testPlans.form.save' : 'testPlans.form.saveCreate') | translate }}
          </button>
        </div>
      }
    </div>
  </div>

  @if (loading()) {
    <p class="tpf-hint">{{ 'testPlans.form.loading' | translate }}</p>
  } @else if (loadError()) {
    <p class="tpf-hint tpf-hint--error">{{ 'testPlans.form.loadError' | translate }}</p>
  } @else {
    <form [formGroup]="form" class="tpf-body">
      <div class="tpf-main">
        <div class="tpf-card">
          <div class="tpf-card__body">
            <div class="tpf-section-title">
              <div class="tpf-section-title__title">{{ 'testPlans.form.basics.title' | translate }}</div>
              <div class="tpf-section-title__subtitle">{{ 'testPlans.form.basics.subtitle' | translate }}</div>
            </div>
            <div class="tpf-fields">
              <mat-form-field appearance="outline" class="tpf-field tpf-field--wide">
                <mat-label>{{ 'testPlans.form.fields.title' | translate }}</mat-label>
                <input
                  matInput
                  formControlName="title"
                  [attr.maxlength]="fieldLengths.NAME_MAX"
                  required
                  aria-required="true"
                />
                @if (form.controls.title.hasError('required') && form.controls.title.touched) {
                  <mat-error>{{ 'testPlans.form.fields.titleRequired' | translate }}</mat-error>
                }
              </mat-form-field>
              @if (releasesEmpty()) {
                <p class="tpf-release-empty" data-testid="release-empty-hint">
                  {{ 'testPlans.form.fields.releaseEmpty' | translate }}
                </p>
              } @else {
                <mat-form-field appearance="outline" class="tpf-field">
                  <mat-label>{{ 'testPlans.form.fields.release' | translate }}</mat-label>
                  <mat-select
                    formControlName="releaseId"
                    data-testid="release-select"
                    required
                    [placeholder]="'testPlans.form.fields.releasePlaceholder' | translate"
                  >
                    @for (release of releases(); track release.id) {
                      <mat-option [value]="release.id">{{ release.id }}</mat-option>
                    }
                  </mat-select>
                  @if (form.controls.releaseId.hasError('required') && form.controls.releaseId.touched) {
                    <mat-error>{{ 'testPlans.form.fields.releaseRequired' | translate }}</mat-error>
                  }
                </mat-form-field>
              }
              <mat-form-field appearance="outline" class="tpf-field">
                <mat-label>{{ 'testPlans.form.fields.dueDate' | translate }}</mat-label>
                <input matInput type="date" formControlName="dueDate" />
              </mat-form-field>
            </div>
          </div>
        </div>

        <div class="tpf-card">
          <div class="tpf-card__body">
            <div class="tpf-section-title">
              <div class="tpf-section-title__title">
                {{ 'testPlans.form.picker.title' | translate }}
                <span class="tpf-section-title__required" aria-hidden="true">*</span>
              </div>
              <div class="tpf-section-title__subtitle">
                {{
                  'testPlans.form.picker.subtitleRequired'
                    | translate: { selected: selectedCount(), total: candidatesCount() }
                }}
              </div>
            </div>

            @if (addMode()) {
              <div class="tpf-picker-filters">
                <div class="tpf-picker-search">
                  <mat-icon fontSet="material-icons-outlined" aria-hidden="true">search</mat-icon>
                  <input
                    type="text"
                    [value]="search()"
                    (input)="onSearchInput($event)"
                    [placeholder]="'testPlans.form.picker.searchPlaceholder' | translate"
                    [attr.aria-label]="'testPlans.form.picker.searchAria' | translate"
                  />
                </div>
                <select
                  class="tpf-picker-select"
                  [value]="typeFilter()"
                  (change)="onTypeChange($event)"
                  [attr.aria-label]="'testPlans.form.picker.typeAria' | translate"
                >
                  <option value="">{{ 'testPlans.form.picker.allTypes' | translate }}</option>
                  @for (t of availableTypes(); track t) {
                    <option [value]="t">{{ 'testCases.type.' + t | translate }}</option>
                  }
                </select>
                <select
                  class="tpf-picker-select"
                  [value]="priorityFilter()"
                  (change)="onPriorityChange($event)"
                  [attr.aria-label]="'testPlans.form.picker.priorityAria' | translate"
                >
                  <option value="">{{ 'testPlans.form.picker.allPriorities' | translate }}</option>
                  @for (p of availablePriorities(); track p) {
                    <option [value]="p">{{ 'testCases.priority.' + p | translate }}</option>
                  }
                </select>
              </div>
            }

            <div
              class="tpf-picker-list"
              role="listbox"
              [attr.aria-label]="'testPlans.form.picker.listAria' | translate"
            >
              @for (tc of visibleCandidates(); track tc.id) {
                <div
                  class="tpf-picker-row"
                  [class.tpf-picker-row--on]="isSelected(tc)"
                  role="option"
                  tabindex="0"
                  [attr.aria-selected]="isSelected(tc)"
                  (click)="toggle(tc)"
                  (keydown.enter)="toggle(tc)"
                  (keydown.space)="$event.preventDefault(); toggle(tc)"
                >
                  <div class="tpf-picker-main">
                    <span class="tpf-picker-check" aria-hidden="true">
                      @if (isSelected(tc)) {
                        <svg viewBox="0 0 16 16" width="10" height="10" aria-hidden="true">
                          <path
                            d="M13.5 4.5L6.5 11.5L2.5 7.5"
                            fill="none"
                            stroke="#fff"
                            stroke-width="2.2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                      }
                    </span>
                    @if (tc.key) {
                      <code class="tpf-picker-id">{{ tc.key }}</code>
                    }
                    <span class="tpf-picker-title">{{ tc.title }}</span>
                    <span
                      class="tpf-picker-automation"
                      [class.tpf-picker-automation--auto]="isAutomated(tc.automationStatus)"
                    >
                      <span class="tpf-picker-automation__dot" aria-hidden="true"></span>
                      {{
                        (isAutomated(tc.automationStatus)
                          ? 'testPlans.form.picker.automated'
                          : 'testPlans.form.picker.manual'
                        ) | translate
                      }}
                    </span>
                    <span class="tpf-picker-type">{{ 'testCases.type.' + tc.type | translate }}</span>
                    <span class="tpf-picker-req">
                      @if (tc.requirementLabel) {
                        {{ tc.requirementLabel }}
                      }
                    </span>
                    @if (isSelected(tc) && stepTotal(tc) > 0) {
                      <button
                        type="button"
                        class="tpf-picker-steps-toggle"
                        [class.tpf-picker-steps-toggle--open]="isStepExpanded(tc)"
                        (click)="toggleStepsExpanded(tc, $event)"
                        [attr.aria-expanded]="isStepExpanded(tc)"
                        [attr.aria-label]="'testPlans.form.picker.stepsButtonAria' | translate: { title: tc.title }"
                      >
                        {{
                          'testPlans.form.picker.stepsButton'
                            | translate: { active: activeStepCount(tc), total: stepTotal(tc) }
                        }}
                        <mat-icon fontSet="material-icons-outlined" aria-hidden="true">chevron_right</mat-icon>
                      </button>
                    }
                  </div>

                  @if (isSelected(tc) && isStepExpanded(tc)) {
                    <div
                      class="tpf-picker-steps"
                      role="none"
                      (click)="$event.stopPropagation()"
                      (keydown)="$event.stopPropagation()"
                    >
                      <div class="tpf-picker-steps__label">
                        {{ 'testPlans.form.picker.stepsHeading' | translate }}
                      </div>
                      @if (isStepLoading(tc)) {
                        <p class="tpf-picker-steps__hint">
                          {{ 'testPlans.form.picker.stepsLoading' | translate }}
                        </p>
                      } @else if (stepsFor(tc).length === 0) {
                        <p class="tpf-picker-steps__hint">
                          {{ 'testPlans.form.picker.stepsEmpty' | translate }}
                        </p>
                      } @else {
                        @for (step of stepsFor(tc); track step.id; let i = $index) {
                          <!-- eslint-disable-next-line @angular-eslint/template/interactive-supports-focus -->
                          <label
                            class="tpf-step-row"
                            [class.tpf-step-row--on]="isStepActive(tc, i)"
                            (click)="$event.stopPropagation()"
                            (keydown)="$event.stopPropagation()"
                          >
                            <span class="tpf-step-check" aria-hidden="true">
                              @if (isStepActive(tc, i)) {
                                <svg viewBox="0 0 16 16" width="9" height="9" aria-hidden="true">
                                  <path
                                    d="M13.5 4.5L6.5 11.5L2.5 7.5"
                                    fill="none"
                                    stroke="#fff"
                                    stroke-width="2.4"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                  />
                                </svg>
                              }
                            </span>
                            <input
                              type="checkbox"
                              class="tpf-step-row__input"
                              [checked]="isStepActive(tc, i)"
                              (change)="toggleStep(tc, i, $event)"
                              [attr.aria-label]="
                                'testPlans.form.picker.stepAria'
                                  | translate: { index: i + 1, title: step.title || step.action || '' }
                              "
                            />
                            <span class="tpf-step-row__index">{{ i + 1 }}</span>
                            <span class="tpf-step-row__title">
                              {{ step.title || step.action || ('testPlans.form.picker.stepUnnamed' | translate) }}
                            </span>
                          </label>
                        }
                      }
                    </div>
                  }
                </div>
              } @empty {
                <p class="tpf-picker-empty">
                  @if (addMode()) {
                    {{ 'testPlans.form.picker.empty' | translate }}
                  } @else {
                    {{ 'testPlans.form.picker.selectedEmpty' | translate }}
                  }
                </p>
              }
            </div>

            <div class="tpf-picker-modeswitch">
              @if (!addMode()) {
                <button type="button" class="tpf-picker-modeswitch__add" (click)="enterAddMode()">
                  <mat-icon aria-hidden="true">add</mat-icon>
                  {{ 'testPlans.form.picker.addMore' | translate }}
                </button>
              } @else if (isEdit) {
                <button
                  type="button"
                  class="tpf-picker-modeswitch__apply"
                  [disabled]="!hasTestCases()"
                  (click)="exitAddMode()"
                >
                  <mat-icon aria-hidden="true">check</mat-icon>
                  {{ 'testPlans.form.picker.applySelection' | translate }}
                </button>
              }
            </div>
          </div>
        </div>
      </div>

      <aside class="tpf-sidebar">
        <div class="tpf-card">
          <div class="tpf-card__body tpf-card__body--sidebar">
            <div class="tpf-card__inline-header">{{ 'testPlans.form.sidebar.properties' | translate }}</div>
            <app-person-select
              formControlName="assigneeUserId"
              [label]="'testPlans.form.fields.assignee' | translate"
              [options]="personOptions()"
              [required]="true"
              [allowNone]="false"
            />
            @if (form.controls.assigneeUserId.hasError('required') && form.controls.assigneeUserId.touched) {
              <p class="tpf-sidebar__hint tpf-sidebar__hint--error">
                {{ 'testPlans.form.fields.assigneeRequired' | translate }}
              </p>
            }
          </div>
        </div>

        <div class="tpf-card">
          <div class="tpf-card__body tpf-card__body--sidebar">
            <div class="tpf-card__inline-header">{{ 'testPlans.form.sidebar.summary' | translate }}</div>
            <div class="tpf-meta-row">
              <span class="tpf-meta-row__label">{{ 'testPlans.form.sidebar.testCases' | translate }}</span>
              <span class="tpf-meta-row__value">{{ selectedCount() }}</span>
            </div>
            <div class="tpf-meta-row">
              <span class="tpf-meta-row__label">{{ 'testPlans.form.sidebar.release' | translate }}</span>
              <span class="tpf-meta-row__value" data-testid="tpf-summary-release">
                {{ selectedReleaseLabel() ?? '—' }}
              </span>
            </div>
            <div class="tpf-meta-row">
              <span class="tpf-meta-row__label">{{ 'testPlans.form.sidebar.executionKind' | translate }}</span>
              <span class="tpf-meta-row__value">
                {{ 'testPlans.form.sidebar.kind.' + executionKind() | translate }}
              </span>
            </div>
            <div class="tpf-meta-row">
              <span class="tpf-meta-row__label">{{ 'testPlans.form.sidebar.due' | translate }}</span>
              <span class="tpf-meta-row__value">
                @if (form.controls.dueDate.value) {
                  {{ form.controls.dueDate.value | date: 'dd.MM.yyyy' }}
                } @else {
                  —
                }
              </span>
            </div>
            <div class="tpf-sidebar__actions" role="status" aria-live="polite">
              <button
                type="button"
                class="tpf-sidebar__run"
                data-testid="plan-execute-btn"
                [class.tpf-sidebar__run--running]="showRunning()"
                [disabled]="!canRun()"
                [attr.aria-label]="
                  (showRunning() ? 'testPlans.form.sidebar.running' : 'testPlans.form.sidebar.run') | translate
                "
                (click)="onRun()"
              >
                @if (showRunning()) {
                  <mat-progress-spinner
                    class="tpf-sidebar__spinner"
                    diameter="14"
                    mode="indeterminate"
                    aria-hidden="true"
                  ></mat-progress-spinner>
                  {{ 'testPlans.form.sidebar.running' | translate }}
                } @else {
                  <mat-icon aria-hidden="true">play_arrow</mat-icon>
                  {{ 'testPlans.form.sidebar.run' | translate }}
                }
              </button>
              @if (showRunCreated()) {
                <div class="tpf-sidebar__run-created" role="status" data-testid="plan-run-created">
                  <mat-icon fontSet="material-icons-outlined" aria-hidden="true">check_circle</mat-icon>
                  <a
                    class="tpf-sidebar__run-link"
                    [routerLink]="createdRunLink().commands"
                    [queryParams]="createdRunLink().queryParams"
                  >
                    @if (createdRun(); as run) {
                      {{ 'testPlans.form.sidebar.runCreated' | translate: { key: run.key } }}
                    } @else {
                      {{ 'testPlans.form.sidebar.runCreatedNoKey' | translate }}
                    }
                  </a>
                </div>
              } @else if (isDone()) {
                <div class="tpf-sidebar__done" role="status">
                  <mat-icon fontSet="material-icons-outlined" aria-hidden="true">check_circle</mat-icon>
                  {{ 'testPlans.form.sidebar.done' | translate }}
                </div>
              } @else if (isFailed()) {
                <div class="tpf-sidebar__failed" role="alert">
                  <mat-icon fontSet="material-icons-outlined" aria-hidden="true">error</mat-icon>
                  {{ execution()?.errorMessage || ('testPlans.form.sidebar.failed' | translate) }}
                </div>
              }
              <button
                type="button"
                class="tpf-sidebar__report"
                [disabled]="!canOpenResults()"
                [attr.aria-label]="'testPlans.form.sidebar.report' | translate"
                [attr.title]="canOpenResults() ? null : ('testPlans.form.sidebar.reportDisabledTooltip' | translate)"
                (click)="onOpenReport()"
              >
                <mat-icon fontSet="material-icons-outlined" aria-hidden="true">shield</mat-icon>
                {{ 'testPlans.form.sidebar.report' | translate }}
              </button>
            </div>
            @if (executionError()) {
              <p class="tpf-sidebar__hint tpf-sidebar__hint--error">
                {{ executionErrorKey() | translate }}
              </p>
            }
            @if (!isEdit) {
              <p class="tpf-sidebar__hint">{{ 'testPlans.form.sidebar.saveFirst' | translate }}</p>
            }
          </div>
        </div>
      </aside>
    </form>

    @if (saveError()) {
      <p class="tpf-hint tpf-hint--error">{{ saveErrorKey() | translate }}</p>
    }
  }
</div>

<lib-modal
  [title]="'common.unsavedChanges.title' | translate"
  [text]="'testPlans.form.confirmLeave' | translate"
  [visible]="showDeactivateConfirm()"
  [saveText]="'common.unsavedChanges.leave' | translate"
  [cancelText]="'common.unsavedChanges.stay' | translate"
  (save)="onDeactivateConfirmed()"
  (cancel)="onDeactivateCancelled()"
  (visibleChange)="!$event && onDeactivateCancelled()"
></lib-modal>
